/**
 * @fileoverview Gestion des agents backoffice.
 *
 * Cette page reste centrée sur le cycle de vie des comptes agents :
 * invitation, statut, sessions et dernière connexion. La gouvernance des
 * rôles et permissions vit dans une page dédiée pour éviter de mélanger les
 * responsabilités d'administration.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  DataCard,
  DataTable,
  EmptyState,
  FormField,
  Input,
  PageHeader,
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@gnpc/ui';
import { Mail, ShieldCheck, UserPlus, UserX } from 'lucide-react';
import { toast } from 'sonner';
import {
  BACKOFFICE_ROLES,
  CURRENT_AGENT,
  ROLE_LABELS,
  USERS,
  formatDateTime,
  type BackofficeRole,
  type BackofficeUser,
} from '../data/backoffice.ts';
import { CapabilityGuard } from '../features/auth/CapabilityProvider.tsx';

/**
 * Message du garde-fou d'auto-désactivation — affiché en tooltip natif sur
 * le bouton désactivé de la ligne de l'agent connecté.
 */
const SELF_DISABLE_MESSAGE = 'Vous ne pouvez pas désactiver votre propre compte';

/**
 * Garde-fou : un agent ne peut jamais désactiver SON PROPRE compte (il se
 * verrouillerait hors de la console). Même philosophie que la règle des
 * 4 yeux de `RefundsPage` (`isOwnRefundRequest`) : l'UI bloque l'action,
 * le backend refera ce contrôle côté serveur.
 */
function isOwnAccount(user: BackofficeUser): boolean {
  return user.email === CURRENT_AGENT.email;
}

const USER_STATUS_LABELS: Record<BackofficeUser['status'], string> = {
  ACTIVE: 'Actif',
  INVITED: 'Invité',
  DISABLED: 'Désactivé',
};

/** Rôles financiers pour lesquels la MFA est obligatoire (C-30). */
const FINANCIAL_ROLES: BackofficeRole[] = ['ADMIN', 'FINANCE_VALIDATOR', 'SUPERVISOR'];

function requiresMfa(role: BackofficeRole): boolean {
  return FINANCIAL_ROLES.includes(role);
}

export function UsersPage(): JSX.Element {
  const [selectedRole, setSelectedRole] = useState<BackofficeRole>('AGENT');
  /**
   * Copie locale mutable du mock : la désactivation est LOGIQUE (statut
   * `DISABLED`), jamais une suppression physique — l'historique d'audit et
   * les références au compte restent intacts.
   */
  const [users, setUsers] = useState<BackofficeUser[]>(USERS);
  const [userToDisable, setUserToDisable] = useState<BackofficeUser | null>(null);

  const confirmDisableUser = () => {
    if (!userToDisable) return;
    setUsers((current) =>
      current.map((user) =>
        user.id === userToDisable.id ? { ...user, status: 'DISABLED' as const } : user,
      ),
    );
    toast.success('Agent désactivé', {
      description: `${userToDisable.fullName} ne peut plus se connecter — le compte est conservé pour l'audit.`,
    });
    setUserToDisable(null);
  };

  const columns = useMemo<ColumnDef<BackofficeUser>[]>(
    () => [
      {
        accessorKey: 'fullName',
        header: 'Agent',
        cell: ({ row }) => (
          <div>
            <p className="text-quipux-navy-950 font-medium">{row.original.fullName}</p>
            <p className="text-muted-foreground text-xs">{row.original.email}</p>
          </div>
        ),
      },
      { accessorKey: 'role', header: 'Rôle', cell: ({ row }) => ROLE_LABELS[row.original.role] },
      {
        accessorKey: 'status',
        header: 'Statut',
        cell: ({ row }) => (
          <Badge variant={row.original.status === 'ACTIVE' ? 'default' : 'outline'}>
            {USER_STATUS_LABELS[row.original.status]}
          </Badge>
        ),
      },
      { accessorKey: 'activeSessions', header: 'Sessions' },
      {
        id: 'mfa',
        header: 'MFA',
        cell: ({ row }) => {
          if (row.original.mfaEnabled) {
            return <span className="text-status-paid-800 text-sm font-medium">Activée</span>;
          }
          if (requiresMfa(row.original.role)) {
            return <span className="text-status-failed-800 text-sm font-medium">Requise</span>;
          }
          return <span className="text-muted-foreground text-sm">—</span>;
        },
      },
      {
        accessorKey: 'lastLoginAt',
        header: 'Dernière connexion',
        cell: ({ row }) =>
          row.original.lastLoginAt ? formatDateTime(row.original.lastLoginAt) : 'Jamais',
      },
      {
        id: 'actions',
        // En-tête non vide pour l'accessibilité : intitulé exposé aux
        // technologies d'assistance mais masqué visuellement (axe
        // `empty-table-header` interdit un `<th>` sans nom accessible).
        header: () => <span className="sr-only">Actions</span>,
        cell: ({ row }) => {
          if (row.original.status !== 'ACTIVE') return null;
          const isSelf = isOwnAccount(row.original);
          return (
            // Capacité FINE `user:disable` : la désactivation est une action
            // sensible distincte de la consultation (`user:read`) et de
            // l'invitation (`user:invite`) — chaque verbe a sa propre garde.
            <CapabilityGuard capability="user:disable">
              <Button
                size="sm"
                variant="outline"
                disabled={isSelf}
                title={isSelf ? SELF_DISABLE_MESSAGE : undefined}
                onClick={() => {
                  // Ceinture ET bretelles : le bouton est désactivé, mais on ne
                  // déclenche jamais l'AlertDialog pour son propre compte.
                  if (!isSelf) setUserToDisable(row.original);
                }}
                aria-label={
                  isSelf ? SELF_DISABLE_MESSAGE : `Désactiver le compte de ${row.original.fullName}`
                }
              >
                <UserX className="size-4" />
                Désactiver
              </Button>
            </CapabilityGuard>
          );
        },
      },
    ],
    [],
  );

  return (
    <Sheet>
      <div className="grid gap-6">
        <PageHeader
          eyebrow="Administration"
          title="Gérer les agents"
          description="Inviter les agents, suivre leur statut et contrôler les sessions actives."
          actions={
            <div className="flex flex-wrap gap-2">
              <Button asChild variant="outline">
                <Link to="/roles-permissions">
                  <ShieldCheck className="size-4" />
                  Rôles et permissions
                </Link>
              </Button>
              {/* Capacité FINE `user:invite` : ouvrir le tiroir d'invitation
                * est réservé aux agents habilités à créer des comptes, sans
                * leur donner pour autant le droit de désactiver. */}
              <CapabilityGuard capability="user:invite">
                <SheetTrigger asChild>
                  <Button>
                    <UserPlus className="size-4" />
                    Inviter un agent
                  </Button>
                </SheetTrigger>
              </CapabilityGuard>
            </div>
          }
        />

        <section className="grid gap-6">
          <DataTable
            columns={columns}
            data={users}
            mobileCardLabel="Agents backoffice"
            mobileCard={(user) => (
              <DataCard
                eyebrow={ROLE_LABELS[user.role]}
                title={user.fullName}
                description={user.email}
                aside={
                  <Badge variant={user.status === 'ACTIVE' ? 'default' : 'outline'}>
                    {USER_STATUS_LABELS[user.status]}
                  </Badge>
                }
                meta={
                  <>
                    <span className="text-muted-foreground">{user.activeSessions} session(s)</span>
                    <span className="text-muted-foreground">
                      {user.capabilities.length} capacité(s)
                    </span>
                    <span className="text-muted-foreground">
                      {user.lastLoginAt ? formatDateTime(user.lastLoginAt) : 'Jamais connecté'}
                    </span>
                  </>
                }
                footer={
                  user.status === 'ACTIVE' ? (
                    // Même capacité fine `user:disable` que la colonne desktop :
                    // la garde suit l'action, pas le point d'affichage.
                    <CapabilityGuard capability="user:disable">
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full"
                        // Même garde-fou que la colonne d'actions desktop :
                        // jamais d'auto-désactivation, dialog jamais ouvert.
                        disabled={isOwnAccount(user)}
                        title={isOwnAccount(user) ? SELF_DISABLE_MESSAGE : undefined}
                        aria-label={
                          isOwnAccount(user)
                            ? SELF_DISABLE_MESSAGE
                            : `Désactiver le compte de ${user.fullName}`
                        }
                        onClick={() => {
                          if (!isOwnAccount(user)) setUserToDisable(user);
                        }}
                      >
                        <UserX className="size-4" />
                        Désactiver
                      </Button>
                    </CapabilityGuard>
                  ) : undefined
                }
              />
            )}
            emptyState={<EmptyState title="Aucun agent" description="Les invitations apparaîtront ici." />}
            pagination={{ label: `${users.length} agent(s)`, hasNextPage: false }}
          />
        </section>
      </div>

      {/* Le contenu du tiroir suit la même garde que son déclencheur :
        * sans `user:invite`, ni le bouton ni le formulaire ne sont montés. */}
      <CapabilityGuard capability="user:invite">
      <SheetContent className="w-[calc(100vw-2rem)] overflow-y-auto p-0 sm:max-w-md">
        <SheetHeader className="border-border border-b p-5 pr-12">
          <SheetTitle>Inviter un agent</SheetTitle>
          <SheetDescription>
            L'agent recevra un email d'invitation et configurera son accès à la première connexion.
          </SheetDescription>
        </SheetHeader>

        <div className="grid gap-4 p-5">
          <FormField label="Email professionnel" required>
            <Input leftIcon={<Mail />} placeholder="agent@quipux.ci" />
          </FormField>
          <FormField label="Rôle de départ" required>
            <select
              value={selectedRole}
              onChange={(event) => setSelectedRole(event.target.value as BackofficeRole)}
              className="border-input bg-background h-10 w-full rounded-md border px-3 text-sm"
            >
              {BACKOFFICE_ROLES.map((role) => (
                <option key={role} value={role}>
                  {ROLE_LABELS[role]}
                </option>
              ))}
            </select>
          </FormField>
          <div className="rounded-lg border border-border p-4 text-sm">
            <p className="text-quipux-navy-950 font-medium">Accès initial</p>
            <p className="text-muted-foreground mt-1 leading-5">
              Le backend génère les consignes de connexion et les envoie par email.
            </p>
          </div>
          <p className="text-muted-foreground text-xs leading-5">
            Les droits associés au rôle se configurent dans la page dédiée aux rôles et permissions.
          </p>
          {/* Exigence MFA pour les rôles financiers (C-30). */}
          {requiresMfa(selectedRole) ? (
            <p className="text-status-failed-800 text-xs leading-5">
              La double authentification (MFA) est obligatoire pour ce rôle financier ; l'agent devra
              la configurer à la première connexion.
            </p>
          ) : null}
        </div>

        <SheetFooter className="border-border border-t p-5">
          <SheetClose asChild>
            <Button
              onClick={() =>
                toast.success('Invitation envoyée', {
                  description: `Rôle de départ : ${ROLE_LABELS[selectedRole]} — l'agent recevra les consignes de connexion par email.`,
                })
              }
            >
              <UserPlus className="size-4" />
              Envoyer l'invitation
            </Button>
          </SheetClose>
          <SheetClose asChild>
            <Button variant="outline">Annuler</Button>
          </SheetClose>
        </SheetFooter>
      </SheetContent>
      </CapabilityGuard>

      <AlertDialog
        open={userToDisable !== null}
        onOpenChange={(open) => {
          if (!open) setUserToDisable(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Désactiver {userToDisable?.fullName ?? 'cet agent'} ?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Le compte sera désactivé sans suppression : les sessions seront fermées, mais
              l'historique d'audit et les références au compte restent intacts. Un administrateur
              pourra le réactiver plus tard.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDisableUser}>Désactiver</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Sheet>
  );
}
